// HotelDetails.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Axios from "axios";

import {
    Autocomplete,
    Box,
    Button,
    Container,
    Divider,
    Drawer,
    Fab,
    IconButton,
    InputAdornment,
    ListItem,
    ListItemText,
    Modal,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    TextField,
    Typography,
    Avatar,
} from "@mui/material";

import {
    ARO_ADMIN,
    GBH_ID,
    getHotelName,
    HOTELS_INFO,
    SanitizeHotelID,
    URLAPI,
} from "../configuration";

import Icons from "../helpers/icons";
import DataLoader from "../components/DataLoader";
import HotelContactsTable from "../components/HotelContactsTable";
import CheckShow from "../components/shared/CheckShow";
import HotelExternalsTable from "../components/HotelExternalsTable";
import { selectHotels } from "../features/hotels/hotelsApi";

const drawerInnerWidth = "calc(100% - 16px)";

//   _   _  ___ _____ _____ _       ____  _____ _____  _    ___ _     ____
//  | | | |/ _ \_   _| ____| |     |  _ \| ____|_   _|/ \  |_ _| |   / ___|
//  | |_| | | | || | |  _| | |     | | | |  _|   | | / _ \  | || |   \___ \
//  |  _  | |_| || | | |___| |___  | |_| | |___  | |/ ___ \ | || |___ ___) |
//  |_| |_|\___/ |_| |_____|_____| |____/|_____| |_/_/   \_\___|_____|____/
const HotelDetailsScreen = () => {
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { auth } = useSelector((state) => state);
    const { dataSimple } = useSelector((state) => state);

    const [modalOpen, setModalOpen] = useState(false); // controls HotelManagersModal
    const [modalCreateOpen, setModalCreateOpen] = useState(false);

    const our = React.useMemo(
        () =>
            (dataSimple?.data?.Hotels ?? []).find(
                ({ HotelID }) => String(HotelID) === String(hotelID),
            ),
        [dataSimple, hotelID],
    );
    const address = React.useMemo(() => {
        if (!our) return "";
        return [
            our.Address1,
            our.City,
            our.Address2,
            our.Address3,
            our.PostalCode,
            our.CountryName,
        ]
            .filter(Boolean)
            .join("\n");
    }, [our]);

    // MANAGER
    const [pendingManager, setPendingManager] = useState(null); // draft value
    const [saveLoading, setSaveLoading] = useState(false);
    const [manager, setManager] = useState(null);
    const [loadingMgr, setLoadingMgr] = useState(false);
    const [mgrError, setMgrError] = useState(null);

    useEffect(() => {
        if (!hotelID) return;
        setLoadingMgr(true);
        setMgrError(null);

        Axios.get(
            `${URLAPI}/hotels/${SanitizeHotelID(hotelID, GBH_ID)}/managers`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth.user.jwt}`,
                },
            },
        )
            .then(({ data }) => {
                const mgrs = data.data;
                setManager(mgrs?.[0] ?? null);
            })
            .catch((err) => {
                console.error("Error fetching account manager:", err);
                setManager(null);
                setMgrError("Error loading account manager");
            })
            .finally(() => setLoadingMgr(false));
    }, [hotelID]);

    // DRAWER
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [allManagers, setAllManagers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const filteredManagers = React.useMemo(
        () =>
            allManagers.filter((mgr) =>
                `${mgr.manager_name} ${mgr.manager_email}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
            ),
        [allManagers, searchTerm],
    );

    // Fetch all managers when drawer opens
    useEffect(() => {
        if (!drawerOpen) return;
        Axios.get(`${URLAPI}/account-managers`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.user.jwt}`,
            },
        })
            .then((res) => setAllManagers(res.data.data))
            .catch((err) => console.error(err));
    }, [drawerOpen]);

    useEffect(() => {
        if (drawerOpen) {
            setPendingManager(manager); // start clean
        }
    }, [drawerOpen, manager]);
    const dirty = React.useMemo(
        () =>
            (pendingManager?.manager_id || null) !==
            (manager?.manager_id || null),
        [pendingManager, manager],
    );

    const handleSave = () => {
        if (!dirty) {
            setDrawerOpen(false);
            return;
        }
        setSaveLoading(true);

        const headers = { Authorization: `Bearer ${auth.user.jwt}` };
        const hotelPath = `${URLAPI}/hotels/${SanitizeHotelID(
            hotelID,
            GBH_ID,
        )}/managers`;

        // Build promise chain: remove (if needed) ➜ add (if needed)
        const removePromise =
            manager && pendingManager?.manager_id !== manager.manager_id
                ? Axios.delete(hotelPath, {
                      headers,
                      data: { manager_id: manager.manager_id },
                  })
                : Promise.resolve();

        const addPromise =
            pendingManager && pendingManager?.manager_id !== manager?.manager_id
                ? () =>
                      Axios.post(
                          hotelPath,
                          { manager_id: pendingManager.manager_id },
                          { headers },
                      )
                : () => Promise.resolve();

        removePromise
            .then(addPromise)
            .then(() => {
                // reflect server state locally
                setManager(pendingManager);
                setDrawerOpen(false);
            })
            .catch((err) => {
                console.error(err);
                alert("Could not save changes. Please try again.");
            })
            .finally(() => setSaveLoading(false));
    };

    const requestCloseDrawer = () => {
        if (dirty) {
            const choice = window.confirm(
                "You have unsaved changes.\nPress OK to discard or Cancel to stay.",
            );
            if (!choice) return; // user wants to stay
        }
        setDrawerOpen(false);
    };

    // handle unassign current manager
    const handleRemove = () => {
        if (!manager) return;
        Axios.delete(
            `${URLAPI}/hotels/${SanitizeHotelID(hotelID, GBH_ID)}/managers`,
            {
                headers: {
                    Authorization: `Bearer ${auth.user.jwt}`,
                },
                data: { manager_id: manager.manager_id },
            },
        )
            .then(() => setManager(null))
            .catch((err) => console.error(err));
    };

    // handle assign new manager (and remove old if exist)
    const handleAssign = (newMgr) => {
        const assign = () => {
            Axios.post(
                `${URLAPI}/hotels/${SanitizeHotelID(hotelID, GBH_ID)}/managers`,
                { manager_id: newMgr.manager_id },
                { headers: { Authorization: `Bearer ${auth.user.jwt}` } },
            )
                .then(() => {
                    setManager(newMgr);
                    setDrawerOpen(false);
                })
                .catch((err) => console.error(err));
        };
        if (manager) {
            Axios.delete(
                `${URLAPI}/hotels/${SanitizeHotelID(hotelID, GBH_ID)}/managers`,
                {
                    headers: { Authorization: `Bearer ${auth.user.jwt}` },
                    data: { manager_id: manager.manager_id },
                },
            )
                .then(assign)
                .catch((err) => console.error(err));
        } else {
            assign();
        }
    };
    const { data: hotels = [] } = useSelector(selectHotels);
    return (
        <>
            <DataLoader SimpleData />

            {/*  Main overview area  */}
            <Container>
                {/*  Title  */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box>
                        <Typography sx={{ mt: 2, mb: 2 }} variant="h4">
                            {getHotelName(hotelID, hotels, false)}
                        </Typography>
                        <Typography>
                            Access and manage client profile, contacts, and
                            communication preferences for each hotel. Edit
                            contact list, assign account managers, and ensure
                            up-to-date email preferences for client
                            communication.
                        </Typography>
                    </Box>
                    {/* <Button variant="contained">Save</Button> */}
                </Stack>

                {/*  Hotel header  */}
                <br />
                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    {/*  Logo  */}
                    <Stack
                        direction="row"
                        spacing={6}
                        sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 256,
                                height: 256,
                                borderRadius: "50%",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "grey.300",
                            }}
                        >
                            <Box
                                component="img"
                                src={HOTELS_INFO[hotelID].img}
                                alt={`Logo ${getHotelName(hotelID, hotels, false)}`}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>

                        {/*  Name + address  */}
                        <Box>
                            <Typography
                                sx={{
                                    maxWidth: 300,
                                    whiteSpace: "pre-line",
                                    wordBreak: "break-word",
                                }}
                            >
                                {address}
                            </Typography>
                        </Box>
                    </Stack>

                    {/*  Current manager  */}
                    <Box>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <Typography fontWeight="bold">
                                ACCOUNT MANAGER
                            </Typography>
                            <IconButton onClick={() => setDrawerOpen(true)}>
                                <Icons.Edit />
                            </IconButton>
                        </Stack>
                        <Typography>
                            {loadingMgr
                                ? "Loading account manager..."
                                : manager
                                  ? manager.manager_name
                                  : (mgrError ?? "No account manager assigned")}
                        </Typography>
                        <Typography>
                            {loadingMgr || !manager
                                ? ""
                                : manager.manager_email}
                        </Typography>
                    </Box>
                </Stack>
                <HotelContactsTable hotelID={hotelID} auth={auth} />
                <HotelExternalsTable hotelID={hotelID} auth={auth} />
            </Container>

            {/*  Lateral drawer (existing functionality)  */}
            {/* EDIT ACCOUNT MANAGER DRAWER */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={requestCloseDrawer} // ← uses guarded close
            >
                <Box sx={{ width: 600, p: 6 }}>
                    {/*  Header  */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h5">
                            Edit Account Manager
                        </Typography>
                        <IconButton onClick={requestCloseDrawer}>
                            <Icons.CloseNavbar />
                        </IconButton>
                    </Stack>
                    <Divider sx={{ my: 2 }} />

                    {/*  Current (draft) manager  */}
                    <Typography fontSize="0.8em">
                        Current Account Manager. Only 1 person can be assigned.
                    </Typography>
                    {pendingManager && (
                        <ListItem
                            sx={{
                                width: drawerInnerWidth,
                                mt: 2,
                                ml: 2,
                                mr: 2,
                                border: 1,
                                borderColor: "grey.400",
                                borderRadius: 1,
                                px: 2,
                            }}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    onClick={() => setPendingManager(null)}
                                >
                                    <Icons.Remove />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={pendingManager.manager_name}
                                secondary={pendingManager.manager_email}
                            />
                        </ListItem>
                    )}
                    <br />

                    {/*  Assign new manager (draft only)  */}
                    <Typography fontSize="0.8em">
                        Choose which person you wish to assign as Account
                        Manager
                    </Typography>
                    <Autocomplete
                        sx={{ pt: 2, ml: 2, width: drawerInnerWidth }}
                        options={allManagers}
                        inputValue={searchTerm}
                        onInputChange={(_, v) => setSearchTerm(v)}
                        getOptionLabel={(o) =>
                            `${o.manager_name} (${o.manager_email})`
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search managers"
                                placeholder="Type to search…"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icons.Search />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        )}
                        onChange={(_, v) => v && setPendingManager(v)}
                        noOptionsText="No managers found"
                    />

                    {/*  Candidates list: highlight draft selection            */}
                    <Stack sx={{ ml: 2, mt: 2 }} spacing={1}>
                        {filteredManagers
                            .filter((m) => {
                                if (!manager) return true; // No saved manager yet
                                if (!pendingManager) return true; // Draft cleared → show again
                                if (
                                    manager.manager_id !==
                                    pendingManager.manager_id
                                ) {
                                    return (
                                        m.manager_id !==
                                        pendingManager.manager_id
                                    );
                                }
                                return m.manager_id !== manager.manager_id; // Otherwise, hide saved mgr
                            })
                            .map((mgr) => (
                                <ListItem
                                    key={mgr.manager_id}
                                    sx={{
                                        border: 1,
                                        borderColor:
                                            pendingManager?.manager_id ===
                                            mgr.manager_id
                                                ? "primary.main"
                                                : "grey.400",
                                        borderRadius: 1,
                                        px: 2,
                                        width: drawerInnerWidth,
                                    }}
                                    secondaryAction={
                                        pendingManager?.manager_id ===
                                        mgr.manager_id ? (
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setPendingManager(null)
                                                }
                                            >
                                                <Icons.Remove />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                edge="end"
                                                onClick={() =>
                                                    setPendingManager(mgr)
                                                }
                                            >
                                                <Icons.Add />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemText
                                        primary={mgr.manager_name}
                                        secondary={mgr.manager_email}
                                    />
                                </ListItem>
                            ))}
                    </Stack>

                    {/*  Save / Cancel buttons                                 */}
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        sx={{ mt: 4 }}
                    >
                        <Button
                            onClick={() => {
                                setDrawerOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!dirty || saveLoading}
                            onClick={handleSave}
                        >
                            {saveLoading ? "Saving…" : "Save"}
                        </Button>
                    </Stack>
                </Box>

                {/*  Open all hotels modal */}
                {drawerOpen && (
                    <>
                        <Fab
                            size="medium"
                            color="primary"
                            aria-label="all-hotels"
                            onClick={() => setModalOpen(true)}
                            sx={{
                                position: "fixed",
                                right: 32,
                                bottom: 32,
                                zIndex: 1301, // above Drawer content
                            }}
                        >
                            <Icons.CalendarMap />
                        </Fab>
                        <CheckShow rolesThatCanSee={ARO_ADMIN}>
                            <Fab
                                size="medium"
                                color="secondary"
                                aria-label="create-account-manager" // NEW
                                onClick={() => setModalCreateOpen(true)} // NEW
                                sx={{
                                    position: "fixed",
                                    right: 96,
                                    bottom: 32,
                                    zIndex: 1301,
                                }}
                            >
                                <Icons.Add />
                            </Fab>
                        </CheckShow>
                    </>
                )}
            </Drawer>

            <HotelManagersModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                auth={auth}
            />
            <CreateAccountManagerModal
                open={modalCreateOpen}
                onClose={() => setModalCreateOpen(false)}
                auth={auth}
            />
        </>
    );
};

//      _    _     _       _   _  ___ _____ _____ _     ____
//     / \  | |   | |     | | | |/ _ \_   _| ____| |   / ___|
//    / _ \ | |   | |     | |_| | | | || | |  _| | |   \___ \
//   / ___ \| |___| |___  |  _  | |_| || | | |___| |___ ___) |
//  /_/   \_\_____|_____| |_| |_|\___/ |_| |_____|_____|____/
const HotelManagersModal = ({ open, onClose, auth }) => {
    const { dataSimple } = useSelector((state) => state);
    const hotels = dataSimple?.data?.Hotels ?? [];
    console.log({ hotels });

    const [hotelsManagers, setHotelsManagers] = useState([]); // [{ hotel_id, manager_id, manager_name }]
    const [allManagers, setAllManagers] = useState([]); // full reference list
    const [loading, setLoading] = useState(false);

    // Fetch reference data whenever the modal opens
    useEffect(() => {
        if (!open) return;
        setLoading(true);

        const headers = { Authorization: `Bearer ${auth.user.jwt}` };

        Promise.all([
            Axios.get(`${URLAPI}/hotels/managers`, { headers }), // assumes backend route without hotel_id returns all
            Axios.get(`${URLAPI}/account-managers`, { headers }),
        ])
            .then(([resManagers, resAll]) => {
                setHotelsManagers(resManagers.data.data);
                setAllManagers(resAll.data.data);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    }, [open]);

    // Helper to commit a new manager to a given hotel
    const assignManager = (hotel_id, manager_id) => {
        Axios.post(
            `${URLAPI}/hotels/${hotel_id}/managers`,
            { manager_id },
            { headers: { Authorization: `Bearer ${auth.user.jwt}` } },
        )
            .then(() => {
                // update local state after successful assignment
                const mgrObj = allManagers.find(
                    (m) => m.manager_id === manager_id,
                );
                setHotelsManagers((prev) => {
                    const idx = prev.findIndex((h) => h.hotel_id === hotel_id);
                    if (idx >= 0) {
                        // update existing
                        const copy = [...prev];
                        copy[idx] = {
                            hotel_id,
                            manager_id,
                            manager_name: mgrObj?.manager_name || "—",
                        };
                        return copy;
                    }
                    // add new
                    return [
                        ...prev,
                        {
                            hotel_id,
                            manager_id,
                            manager_name: mgrObj?.manager_name || "—",
                        },
                    ];
                });
            })
            .catch((err) => console.error(err));
    };

    // Prepare rows of two hotels
    const rows2x = React.useMemo(() => {
        const enriched = hotels
            .map((h) => {
                const match =
                    hotelsManagers.find((x) => x.hotel_id === h.HotelID) ?? {};
                return {
                    hotel_id: h.HotelID,
                    hotel_name: h.HotelName ?? "",
                    manager_id: match.manager_id ?? "",
                    manager_name: match.manager_name ?? "—",
                };
            })
            .sort((a, b) => a.hotel_name.localeCompare(b.hotel_name));
        const arr = [];
        for (let i = 0; i < enriched.length; i += 2)
            arr.push([enriched[i], enriched[i + 1] ?? null]);
        return arr;
    }, [hotels, hotelsManagers]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: "background.paper",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "85%",
                    maxHeight: "80%",
                    overflow: "auto",
                    borderRadius: 2,
                    boxShadow: 24,
                }}
            >
                <Typography variant="h6" mb={2}>
                    All Hotels - Account Managers
                </Typography>

                {loading ? (
                    <Typography variant="body2">Loading…</Typography>
                ) : (
                    <Paper variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Hotel Name</TableCell>
                                    <TableCell>Account Manager</TableCell>

                                    {/* small spacer */}
                                    <TableCell
                                        sx={{ width: "15%", border: "none" }}
                                    />
                                    <TableCell></TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Hotel Name</TableCell>
                                    <TableCell>Account Manager</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows2x.map((pair, idx) => (
                                    <TableRow key={idx}>
                                        {/*  First hotel  */}
                                        {pair[0] && (
                                            <>
                                                <TableCell
                                                    sx={{ fontSize: "0.75rem" }}
                                                >
                                                    <Avatar
                                                        alt={`Hotel Logo ${pair[0].hotel_name}`}
                                                        src={
                                                            (
                                                                HOTELS_INFO[
                                                                    pair[0]
                                                                        .hotel_id
                                                                ] ?? {}
                                                            ).img ?? ""
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {pair[0].hotel_id}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {pair[0].hotel_name}
                                                </TableCell>
                                                <TableCell
                                                    sx={{ minWidth: 140 }}
                                                >
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={
                                                            pair[0].manager_id
                                                        }
                                                        displayEmpty
                                                        onChange={(e) =>
                                                            assignManager(
                                                                pair[0]
                                                                    .hotel_id,
                                                                e.target.value,
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value="">
                                                            None
                                                        </MenuItem>
                                                        {allManagers.map(
                                                            (m) => (
                                                                <MenuItem
                                                                    key={
                                                                        m.manager_id
                                                                    }
                                                                    value={
                                                                        m.manager_id
                                                                    }
                                                                >
                                                                    {
                                                                        m.manager_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                </TableCell>
                                            </>
                                        )}

                                        {/*  Spacer  */}
                                        <TableCell
                                            sx={{
                                                width: "15%",
                                                border: "none",
                                            }}
                                        />

                                        {/*  Second hotel (may be null if odd total)  */}
                                        {pair[1] ? (
                                            <>
                                                <TableCell
                                                    sx={{ fontSize: "0.75rem" }}
                                                >
                                                    <Avatar
                                                        alt={`Hotel Logo ${pair[0].hotel_name}`}
                                                        src={
                                                            (
                                                                HOTELS_INFO[
                                                                    pair[1]
                                                                        .hotel_id
                                                                ] ?? {}
                                                            ).img ?? ""
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {pair[1].hotel_id}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {pair[1].hotel_name}
                                                </TableCell>
                                                <TableCell
                                                    sx={{ minWidth: 140 }}
                                                >
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={
                                                            pair[1].manager_id
                                                        }
                                                        displayEmpty
                                                        onChange={(e) =>
                                                            assignManager(
                                                                pair[1]
                                                                    .hotel_id,
                                                                e.target.value,
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value="">
                                                            None
                                                        </MenuItem>
                                                        {allManagers.map(
                                                            (m) => (
                                                                <MenuItem
                                                                    key={
                                                                        m.manager_id
                                                                    }
                                                                    value={
                                                                        m.manager_id
                                                                    }
                                                                >
                                                                    {
                                                                        m.manager_name
                                                                    }
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell />
                                                <TableCell />
                                                <TableCell />
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </Box>
        </Modal>
    );
};

//    ____ ____  _____    _  _____ _____      _    __  __
//   / ___|  _ \| ____|  / \|_   _| ____|    / \  |  \/  |
//  | |   | |_) |  _|   / _ \ | | |  _|     / _ \ | |\/| |
//  | |___|  _ <| |___ / ___ \| | | |___   / ___ \| |  | |
//   \____|_| \_\_____/_/   \_\_| |_____| /_/   \_\_|  |_|
const CreateAccountManagerModal = ({ open, onClose, auth }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const reset = () => {
        setName("");
        setEmail("");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMsg(null);

        // Basic validation
        if (!name.trim() || !email.trim()) {
            setError("Both name and email are required");
            return;
        }

        setLoading(true);
        Axios.post(
            `${URLAPI}/account-managers`,
            {
                manager_name: name.trim(),
                manager_email: email.trim(),
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth.user.jwt}`,
                },
            },
        )
            .then(() => {
                setSuccessMsg("Account Manager created ✔");
                reset();
                // Auto‑close after short delay so user sees confirmation
                setTimeout(onClose, 1200);
            })
            .catch((err) => {
                console.error(err);
                setError("Could not create. Please try again.");
            })
            .finally(() => setLoading(false));
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: "background.paper",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 420,
                    borderRadius: 2,
                    boxShadow: 24,
                }}
            >
                <Typography variant="h6" mb={2}>
                    Create Account Manager
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={loading}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            required
                            fullWidth
                        />
                        {error && (
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        )}
                        {successMsg && (
                            <Typography color="success.main" variant="body2">
                                {successMsg}
                            </Typography>
                        )}
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="flex-end"
                        >
                            <Button onClick={onClose} disabled={loading}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? "Saving…" : "Create"}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
};

export default HotelDetailsScreen;
